import { useState, useEffect } from 'react'
import { adminAPI } from '../../lib/api'
import type { User } from '../../types'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { useToast } from '../../components/ui/useToast'
import {
  BarChart3, Download, Calendar, TrendingUp, DollarSign,
  Users, Building2, Activity, FileText, Filter, RefreshCw,
  ArrowUp, ArrowDown, Clock, CheckCircle, AlertCircle
} from 'lucide-react'

export function AdminReportsPage() {
  const [user, setUser] = useState<User | null>(null)
  const { notify } = useToast()
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState('30')
  const [reportType, setReportType] = useState('overview')

  // Mock data - in production, fetch from API
  const [stats, setStats] = useState({
    totalRevenue: 125450,
    totalBookings: 3842,
    totalTenants: 47,
    activeUsers: 892,
    revenueGrowth: 18.5,
    bookingsGrowth: 12.3,
    tenantsGrowth: 8.7,
    usersGrowth: 15.2
  })

  useEffect(() => {
    ;(async () => {
      try {
        const sessionRes = await fetch('/api/admin/session', { credentials: 'include' })
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json()
          if (sessionData.success && sessionData.data?.user) {
            setUser(sessionData.data.user)
          }
        }
      } catch {
        // Silent fail
      }
    })()
  }, [])

  const handleExportReport = async (type: string) => {
    setLoading(true)
    try {
      if (type === 'bookings') {
        const blob = await adminAPI.downloadBookingsCsv()
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `global-bookings-${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        notify({ kind: 'success', message: 'Bookings report downloaded' })
      } else if (type === 'revenue') {
        const blob = await adminAPI.downloadRevenueCsv()
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `global-revenue-${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        notify({ kind: 'success', message: 'Revenue report downloaded' })
      }
    } catch (error) {
      notify({ kind: 'error', message: 'Failed to download report' })
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({
    title,
    value,
    icon: Icon,
    growth,
    color = 'orange',
    prefix = ''
  }: {
    title: string
    value: string | number
    icon: any
    growth?: number
    color?: 'orange' | 'blue' | 'green' | 'purple'
    prefix?: string
  }) => {
    const colorClasses = {
      orange: {
        gradient: 'from-orange-50 to-red-50 dark:from-orange-500/5 dark:to-red-500/5',
        border: 'border-orange-200 dark:border-orange-800',
        text: 'text-orange-600 dark:text-orange-400',
        icon: 'from-orange-500 to-red-500'
      },
      blue: {
        gradient: 'from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-600 dark:text-blue-400',
        icon: 'from-blue-500 to-indigo-500'
      },
      green: {
        gradient: 'from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-600 dark:text-green-400',
        icon: 'from-green-500 to-emerald-500'
      },
      purple: {
        gradient: 'from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5',
        border: 'border-purple-200 dark:border-purple-800',
        text: 'text-purple-600 dark:text-purple-400',
        icon: 'from-purple-500 to-pink-500'
      }
    }

    const colors = colorClasses[color]

    return (
      <div className={`card bg-gradient-to-br ${colors.gradient} ${colors.border}`}>
        <div className="card-body">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`text-sm font-medium ${colors.text}`}>{title}</p>
              <p className="text-3xl font-bold mt-2 text-gray-900 dark:text-gray-100">
                {prefix}{typeof value === 'number' ? value.toLocaleString() : value}
              </p>
              {growth !== undefined && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${
                  growth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {growth >= 0 ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : (
                    <ArrowDown className="h-4 w-4" />
                  )}
                  <span className="font-medium">{Math.abs(growth)}% vs last period</span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.icon}`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Global Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Platform-wide analytics and performance metrics
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-orange-600" />
              <span className="font-medium">Filters:</span>
            </div>
            <div className="flex-1 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="form-label text-xs">Date Range</label>
                <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                  <option value="all">All time</option>
                </Select>
              </div>
              <div>
                <label className="form-label text-xs">Report Type</label>
                <Select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                  <option value="overview">Overview</option>
                  <option value="revenue">Revenue Analysis</option>
                  <option value="bookings">Bookings Analysis</option>
                  <option value="tenants">Tenant Performance</option>
                  <option value="users">User Activity</option>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-orange-600" />
          Key Performance Indicators
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Revenue"
            value={stats.totalRevenue}
            icon={DollarSign}
            growth={stats.revenueGrowth}
            color="orange"
            prefix="£"
          />
          <StatCard
            title="Total Bookings"
            value={stats.totalBookings}
            icon={Calendar}
            growth={stats.bookingsGrowth}
            color="blue"
          />
          <StatCard
            title="Active Tenants"
            value={stats.totalTenants}
            icon={Building2}
            growth={stats.tenantsGrowth}
            color="green"
          />
          <StatCard
            title="Active Users"
            value={stats.activeUsers}
            icon={Users}
            growth={stats.usersGrowth}
            color="purple"
          />
        </div>
      </div>

      {/* Export Reports */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Download className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">Export Reports</h2>
        </div>
        <div className="card-body">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Download comprehensive reports for analysis and record-keeping
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 border-2 border-gray-200 dark:border-gray-800 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-500/10">
                  <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Bookings Report</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Complete booking data with timestamps and customer details
                  </p>
                  <Button
                    onClick={() => handleExportReport('bookings')}
                    disabled={loading}
                    className="w-full text-sm"
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 border-2 border-gray-200 dark:border-gray-800 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-500/10">
                  <DollarSign className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Revenue Report</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Detailed revenue breakdown by tenant and time period
                  </p>
                  <Button
                    onClick={() => handleExportReport('revenue')}
                    disabled={loading}
                    className="w-full text-sm"
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 border-2 border-gray-200 dark:border-gray-800 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-500/10">
                  <Building2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Tenant Summary</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Overview of all tenants with activity metrics
                  </p>
                  <Button
                    disabled
                    className="w-full text-sm"
                    variant="outline"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 border-2 border-gray-200 dark:border-gray-800 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-500/10">
                  <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">User Activity</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    User engagement and login statistics
                  </p>
                  <Button
                    disabled
                    className="w-full text-sm"
                    variant="outline"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 border-2 border-gray-200 dark:border-gray-800 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-500/10">
                  <Activity className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">System Logs</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Platform events and error logs
                  </p>
                  <Button
                    disabled
                    className="w-full text-sm"
                    variant="outline"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 border-2 border-gray-200 dark:border-gray-800 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-500/10">
                  <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Custom Report</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Build custom reports with specific metrics
                  </p>
                  <Button
                    disabled
                    className="w-full text-sm"
                    variant="outline"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h2 className="font-semibold text-lg">Top Performing Tenants</h2>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {[
                { name: 'Rhythm Studios London', bookings: 342, revenue: 18500 },
                { name: 'SoundWave Productions', bookings: 298, revenue: 15200 },
                { name: 'Creative Space Hub', bookings: 276, revenue: 14800 },
                { name: 'Music Lab Studios', bookings: 245, revenue: 12900 },
                { name: 'The Practice Room', bookings: 198, revenue: 10200 }
              ].map((tenant, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{tenant.name}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{tenant.bookings} bookings</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-orange-600 dark:text-orange-400">
                      £{tenant.revenue.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold text-lg">Platform Health</h2>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <div className="font-medium text-sm">API Services</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Response time: 45ms</div>
                  </div>
                </div>
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <div className="font-medium text-sm">Database</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Query time: 12ms avg</div>
                  </div>
                </div>
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <div className="font-medium text-sm">Worker Runtime</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">CPU usage: 23%</div>
                  </div>
                </div>
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <div className="font-medium text-sm">Uptime</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">99.98% (30 days)</div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">Excellent</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-xl">
        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
        <div className="flex-1 text-sm">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Report Data</h3>
          <p className="text-blue-800 dark:text-blue-300">
            Reports are generated in real-time from platform data. Export functionality for Bookings and Revenue is currently available.
            Additional report types and custom analytics will be added in future updates.
          </p>
        </div>
      </div>
    </div>
  )
}
